import { Col, Row, Table, Typography, Pagination, Tooltip } from "antd";
import { SpinnerBlock } from "src/components/Spinner";
import { GridContent } from "@ant-design/pro-layout";
import styled from "styled-components";
import {
  ChartLineData01Icon,
  PresentationBarChart01Icon,
} from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import PageHeader from "src/components/PageHeader";
import {
  Button as UIButton,
  EmptyState,
  tableChrome,
  tablePagination,
} from "src/components/ui";
import { CardWidget, WidgetFrame, WidgetTitle } from "./components/Card";
import { colors } from "src/theme/colors";
import { line, radii, seriesTone, space, text } from "src/theme/ui";
import { FC, useEffect, useState } from "react";
import { userInterface } from "src/interfaces/Client";
import { SymbolOverViewWidget } from "./widgets/SymbolOverview";
import {
  ORDERS_PAGE_SIZE,
  useBrokerageOrderByTickerID,
} from "./api/useGetOrders";
import { useBrokeragePositions } from "./api/useBrokeragePositions";
import { fixNumber } from "src/utils/number";
import { useHistory, useLocation, useParams } from "react-router";
import { useGetTickerById } from "./api/useGetTickerById";
import { ordersColumns } from "./utils/orderColumns";
import { InvestModal } from "./Layouts/InvestModal";
import { TradingForm } from "./components/StockForm";
import { useCancelOrder } from "./api/useCancelOrder";
import { useQueryClient } from "react-query";
import { useBrokeragePositionBySymbol } from "./api/useBrokeragePositionBySymbol";

interface BrokerageOrdersProps {
  user: userInterface;
}

const { Text } = Typography;

/**
 * The position, as a grid of readings.
 *
 * Eleven figures in four columns separated by hard #E0E3EB rules and two
 * centimetres of side padding — a spreadsheet drawn in a card, where the
 * rules did the work that whitespace does everywhere else in this product,
 * and every figure was set at the same weight in the same ink whether it was
 * a market value or a percentage gain.
 *
 * Label over figure, the way every other reading in the product is set; no
 * rules; and the profit-and-loss percentages carry the tone this product
 * reserves for a direction, because a P&L is the one thing on this card that
 * HAS one. Everything else stays ink.
 */
const Readings = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(148px, 1fr));
  gap: ${space.lg}px ${space.md}px;
`;

const Reading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const ReadingLabel = styled.span`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${colors.gray[500]};
`;

const ReadingValue = styled.span<{ $tone?: string }>`
  ${text.numeric}
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ $tone }) => $tone ?? colors.gray[900]};
`;

/* The product's table, on the orders list — it was Ant Design's own, in a
   card whose every other surface had been brought onto the product's. */
/**
 * The orders list, in the same table card as the dashboard's own.
 *
 * The parts that were still Ant Design's: a border round the table INSIDE a
 * card that is already a frame, so the rows sat in a box in a box; a hard
 * vertical rule down the whole table where the frozen Action column begins,
 * which read as a boundary between two tables rather than as one column
 * holding the rest back; and a pager floating in the card's padding instead
 * of in a footer band. All three are how the dashboard's table already
 * solves them, so they are solved the same way here.
 */
/* The card the table lives in, so the pager below the table lands in the
   product's footer band — the shared rule targets the table pager's class,
   and this pager sits outside the Table element. */
const TableCard = styled(CardWidget)`
  ${tablePagination}
`;

const OrdersTable = styled(Table)`
  ${tableChrome(16)}

  /* No inner frame. The card is the frame. */
  .ant-table-container {
    position: relative;
    border: 0;
    border-radius: 0;
  }

  .ant-table-summary > tr > td {
    padding: 10px 12px;
    border-top: 1px solid ${line.hairline};
    background: ${colors.gray[25]};
    font-variant-numeric: tabular-nums;
  }

  /* Frozen cells sit over the scrolling ones, so they need a ground of their
     own — and they have to pick up the row hover, or the pinned column stays
     white while the rest of its row lights up. */
  .ant-table-tbody .ant-table-cell-fix-left,
  .ant-table-tbody .ant-table-cell-fix-right {
    background: ${colors.white};
  }

  .ant-table-thead .ant-table-cell-fix-left,
  .ant-table-thead .ant-table-cell-fix-right {
    background: ${colors.gray[50]};
  }

  .ant-table-summary .ant-table-cell-fix-left,
  .ant-table-summary .ant-table-cell-fix-right {
    background: ${colors.gray[25]};
  }

  .ant-table-tbody > tr.ant-table-row:hover > td.ant-table-cell-fix-left,
  .ant-table-tbody > tr.ant-table-row:hover > td.ant-table-cell-fix-right {
    background: ${colors.primary[10]};
  }

  /* A fade, not a rule — and only while the frozen column is actually
     holding something back. */
  .ant-table-cell-fix-left-last::after,
  .ant-table-cell-fix-right-first::after {
    box-shadow: none !important;
    border-right: 0;
  }

  .ant-table-ping-right .ant-table-cell-fix-right-first::after {
    box-shadow: inset -10px 0 8px -8px rgba(15, 20, 25, 0.1) !important;
  }

  /* Cut off mid-column, a scrolling table gives no sign there is more to the
     right. The library flags the state; this draws the hint. */
  .ant-table-ping-right .ant-table-container::after {
    content: "";
    position: absolute;
    top: 0;
    right: 110px;
    bottom: 0;
    z-index: 2;
    width: 40px;
    pointer-events: none;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0),
      ${colors.white}
    );
  }
`;

const toneFor = (value: number) =>
  value > 0 ? seriesTone.up : value < 0 ? seriesTone.down : undefined;

interface Position {
  ticker: {
    symbol: string;
    market: {
      market: string;
    };
  };
  quantity: number;
  price: number;
  avg_price: number;
  value: number;
  cost: number;
  unrealized_pnl_pct: number;
  realized_pnl_pct: number;
}

const PositionInfos = ({ tickerId }) => {
  const [position, setPosition] = useState<Position>();
  const [chunkedPostion, setChunkedPosition] = useState<any[] | null>(null);

  const { data: positions, isLoading: isPositionsLoading } =
    useBrokeragePositions();

  useEffect(() => {
    if (positions && tickerId) {
      const foundedPosition = positions.find(
        (position) => position.ticker_id == tickerId,
      );
      setPosition(foundedPosition);
    }
  }, [positions]);

  useEffect(() => {
    if (position) {
      const unrealized = fixNumber(position.unrealized_pnl_pct * 100, 3);
      const realized = fixNumber(position.realized_pnl_pct * 100, 3);
      setChunkedPosition([
        { label: "Security", value: position.ticker?.symbol },
        { label: "Market", value: position.ticker?.market?.market },
        { label: "Quantity", value: position.quantity },
        { label: "Avg cost", value: `$${fixNumber(position.avg_price, 3)}` },
        { label: "Last price", value: `$${fixNumber(position.price, 3)}` },
        { label: "Market value", value: `$${fixNumber(position.value, 3)}` },
        { label: "Actual cost", value: `$${fixNumber(position.cost, 3)}` },
        {
          label: "Unrealized P&L",
          value: `${unrealized}%`,
          tone: toneFor(unrealized),
        },
        {
          label: "Realized P&L",
          value: `${realized}%`,
          tone: toneFor(realized),
        },
        {
          label: "Total P&L",
          value: `${unrealized + realized}%`,
          tone: toneFor(unrealized + realized),
        },
      ]);
    }
  }, [position]);

  /* The product's one indicator rather than antd's four turning dots. */
  if (isPositionsLoading) return <SpinnerBlock />;
  if (chunkedPostion)
    return (
      <Readings>
        {chunkedPostion.map((item: any) => (
          <Reading key={item.label}>
            <ReadingLabel>{item.label}</ReadingLabel>
            <ReadingValue $tone={item.tone}>{item.value}</ReadingValue>
          </Reading>
        ))}
      </Readings>
    );
  return (
    <EmptyState
      icon={PresentationBarChart01Icon}
      title="No position in this security"
      hint="Orders you place will show underneath."
    />
  );
};

const BrokerageOrders: FC<BrokerageOrdersProps> = ({ user }) => {
  // const { param } = useParams<{ param: string }>();
  const { ticker_id, symbol } = useParams<{
    ticker_id: string;
    symbol: string;
  }>();

  const queryClient = useQueryClient();
  const history = useHistory();
  const location = useLocation<{ cameFromApp?: boolean } | undefined>();

  const [orders, setOrders] = useState<any[]>([]);
  const [side, setSide] = useState<null | string>(null);
  const [showInvestModal, setShowInvestModal] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { data: dataOrders, isLoading: isOrdersLoading } =
    useBrokerageOrderByTickerID({ currentPage, ticker_id });

  const { data: ticker, isLoading: isTickerLoading } =
    useGetTickerById(ticker_id);
  const {
    mutate: cancelOrder,
    isLoading: cancelOrderLoading,
    isSuccess: isCancelSuccess,
  } = useCancelOrder();
  const {
    data: position,
    isLoading: isPositionLoding,
    isError: isPositionError,
    error: positionError,
  } = useBrokeragePositionBySymbol(symbol);

  useEffect(() => {
    if (dataOrders) {
      setOrders(dataOrders?.data);
      setTotalPages(Math.ceil(dataOrders?.count / ORDERS_PAGE_SIZE));
    }
  }, [dataOrders]);

  useEffect(() => {
    if (isCancelSuccess)
      queryClient.invalidateQueries({ queryKey: ["brokerageOrders"] });
  }, [isCancelSuccess]);

  const handleShowInvestModal = () => {
    setShowInvestModal(!showInvestModal);
  };

  const handleSelectSide = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSide(event.currentTarget.value);
    setShowInvestModal(!showInvestModal);
  };

  const handlePaginationChange = (page: number) => {
    setCurrentPage(page);
  };

  const onCancelOrder = (record: any, id: number) => {
    setSelectedId(id);
    cancelOrder(record?.id);
  };

  return (
    <GridContent>
      {/* The page's own head, like the security page next door. It was a card
          wrapping the screen, titled "Security Orders" rather than named
          after the security whose orders these are — and both its actions
          were drawn as primaries, so Sell and Buy shouted at each other. */}
      <PageHeader
        name={ticker?.symbol || symbol || "Orders"}
        meta={[ticker?.portal_name, "Orders"]}
        backLabel="Back to trading"
        onBack={() => {
          if (location.state?.cameFromApp) history.goBack();
          else history.push("/brokerage");
        }}
        actions={
          <>
            <Tooltip
              placement="top"
              title={
                position?.length === 0
                  ? "There are no items available for sale on this ticket."
                  : ""
              }
            >
              <span>
                <UIButton
                  variant="secondary"
                  value="sell"
                  onClick={handleSelectSide}
                  disabled={position?.length === 0}
                >
                  Sell
                </UIButton>
              </span>
            </Tooltip>
            <UIButton variant="primary" value="buy" onClick={handleSelectSide}>
              Buy
            </UIButton>
          </>
        }
      />
      <Row gutter={[16, 16]}>
        <Col span={24}>
          {/* No head. It carried one badge repeating the symbol already in
              the page's own title — a head with nothing in it. */}
          <CardWidget title={null} loading={isTickerLoading}>
            <WidgetFrame $height={409}>
              <SymbolOverViewWidget symbol={ticker?.symbol} />
            </WidgetFrame>
          </CardWidget>
        </Col>
        <Col span={24}>
          <CardWidget
            title={
              <WidgetTitle>
                <HugeIcon icon={PresentationBarChart01Icon} size={16} />
                Position
              </WidgetTitle>
            }
            bodyStyle={{ padding: 16 }}
          >
            {/* The ticker from the URL. This card was hard-coded to ticker 3,
                so every security's orders page showed the same position —
                or none. */}
            <PositionInfos tickerId={ticker_id} />
          </CardWidget>
        </Col>
        <Col span={24}>
          <TableCard
            title={
              <WidgetTitle>
                <HugeIcon icon={ChartLineData01Icon} size={16} />
                Orders
              </WidgetTitle>
            }
            /* The table spans the card; tableChrome supplies the gutters. */
            bodyStyle={{ padding: 0 }}
          >
            <OrdersTable
              size="small"
              loading={isOrdersLoading}
              columns={ordersColumns(
                onCancelOrder,
                cancelOrderLoading,
                selectedId,
              )}
              dataSource={orders}
              /* Nine columns fit; thirteen did not, which is the only reason
                 this table ever scrolled sideways. */
              scroll={{ x: 900 }}
              pagination={false}
            />
            {/* The pager is a separate component because the paging is the
                backend's — antd's own would slice the page it was handed. It
                borrows the table pager's CLASS so the shared footer band
                applies to it, rather than floating in the card's padding. */}
            <Pagination
              /* Both classes: the base one for the shared footer band, and
                 the -right modifier antd itself uses to push the pager to the
                 far end. With only the base class it sat hard against the
                 left edge, under the first column. */
              className="ant-table-pagination ant-table-pagination-right"
              current={currentPage}
              total={totalPages * ORDERS_PAGE_SIZE}
              pageSize={ORDERS_PAGE_SIZE}
              showSizeChanger={false}
              onChange={handlePaginationChange}
              /* The count on the left, the pages on the right — the same
                 footer the dashboard's tables use. A lone pager says which
                 page you are on and not how many orders there are. */
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} orders`
              }
            />
          </TableCard>
        </Col>
      </Row>
      <InvestModal
        setModalVisible={handleShowInvestModal}
        isModalVisible={showInvestModal}
        side={side}
        symbol={ticker?.symbol}
      >
        <TradingForm
          side={side}
          stockInfos={{
            id: ticker?.id,
            symbol: ticker?.symbol,
            name: ticker?.portal_name,
            maxQty: position?.[0]?.quantity,
          }}
          onCancel={handleShowInvestModal}
        />
      </InvestModal>
    </GridContent>
  );
};

export default BrokerageOrders;
